<template>
  <div class="content-list">
    <template v-if="viewState.isLoadingTracks" name="fade" mode="out-in">
      <WaitingBlock />
    </template>

    <template v-else name="fade">
      <div v-if="fileList.length">
        <table
          :class="`table table-stripped table-condensed${viewState.isSorting ? ' is-sorting' : ''}`"
          style
        >
          <colgroup>
            <col span="1" />
            <col span="1" />
            <col span="3" class="col-l" />
            <col class="col-s" />
            <col span="2" class="col-xs" />
            <col span="5" class="col-xs" />
            <col duration />
            <col label class="col-l" />
            <col added />
            <col class="actions" />
          </colgroup>

          <thead class="sticky">
            <tr>
              <th role="rowheader">
                <i
                  :class="['far', selectedFileData.length === 0 ? 'fa-square':'fa-check-square']"
                  @click.prevent="toggleSelection()"
                ></i>
              </th>

              <th v-for="prop in visibleListProperties" :key="`property-${prop.name}`">
                <a
                  href="#"
                  :class="`sort ${prop.name === sort.field ? ' is-active' : ''}`"
                  v-on:click.prevent="order(prop.name)"
                  :disabled="viewState.isSorting"
                >
                  <span v-html="prop.label"></span>

                  <div class="sort-icons" v-if="prop.name !== sort.field || !viewState.isSorting">
                    <i
                      class="fas fa-chevron-up"
                      :style="{ color: prop.name === sort.field && !sort.ascending ? '#000' : '#aaa' }"
                    ></i>
                    <i
                      class="fas fa-chevron-down"
                      :style="{ color: prop.name === sort.field && sort.ascending ? '#000' : '#aaa' }"
                    ></i>
                  </div>

                  <i v-if="prop.name === sort.field && viewState.isSorting" class="fas fa-sync-alt"></i>
                </a>
              </th>

              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="(track, index) in fileList"
              :key="track.uid"
              :class="[{'selected':selectedFiles.tracks[track.uid]!=null, shiftable:shiftSelection.startIndex !=null && shiftSelection.startIndex == index }]"
            >
              <th role="rowheader">
                <span class="rowheader-wrapper">
                  <span :class="['item-bar', {'valid':track.isFilled,'invalid':!track.isFilled}]"></span>

                  <PlayButton class="play-button" :track="track"></PlayButton>

                  <i
                    @click.prevent="toggleTrack(track, $event,index)"
                    :class="['far', selectedFiles.tracks[track.uid] === undefined ? 'fa-square':'fa-check-square']"
                  ></i>
                </span>
              </th>

              <template v-for="prop in visibleListProperties">
                <td :key="`property-${prop.name}`" v-if="prop.name === 'typeID'" class="text-center">
                  <i
                    class="type-icon fa"
                    :class="trackTypesIcons[track.typeID].icon || 'fa-question'"
                    v-tooltip="{ data: trackTypesIcons[track.typeID].text || '', arrowPosition: 'top'}"></i>
                </td>

                <td :key="`property-${prop.name}`" v-if="prop.name === 'title'" class="td-xl">
                  <TextInput
                    :originalValue="track.title"
                    @onValueChanged="updateTrackField([track],'title',$event)"
                  />
                </td>

                <td :key="`property-${prop.name}`" v-if="prop.name === 'artist'" class="td-xl">
                  <TextInput
                    :originalValue="track.artist"
                    @onValueChanged="updateTrackField([track],'artist',$event)"
                  />
                </td>

                <td :key="`property-${prop.name}`" v-if="prop.name === 'artists'" class="td-xl">
                  <ArtistBadgeList
                    :artists="track.artists"
                    :artist-only="track.artist"
                    :readOnly="true"
                    @click.prevent="openParameterSelector('showArtistSelector','modalArtists', [track])"
                  />
                  <i  v-if="track.hiddenArtist" class="fas fa-info-circle" :title="track.hiddenArtist"/>
                </td>

                <td :key="`property-${prop.name}`" v-if="prop.name === 'styles'" class="td-xl">
                  <StyleBadgeList
                    :styles="track.styles"
                    :readOnly="true"
                    @click.prevent="openParameterSelector('showStyleSelector','modalStyles', [track])"
                  />
                </td>

                <td :key="`property-${prop.name}`" v-if="prop.name === 'languages'" class="td-xl">
                  <LanguageBadgeList
                    :languages="track.languages"
                    :readOnly="true"
                    @click.prevent="openParameterSelector('showLanguageSelector','modalLanguages', [track])"
                  />
                </td>

                <td :key="`property-${prop.name}`" v-if="prop.name === 'label'" class="td-xl">
                  <LabelTextInput
                    :readOnly="true"
                    :label="track.label"
                    @click.prevent="openParameterSelector('showLabelSelector','modalLabels', [track])"
                  />
                </td>

                <td :key="`property-${prop.name}`" v-if="prop.name === 'description'" class="td-xl">
                  <TextInput
                    :originalValue="track.description"
                    @onValueChanged="updateTrackField([track],'description',$event)"
                    placeholder="Description"
                  />

                </td>

                <td :key="`property-${prop.name}`" v-if="prop.name === 'tags'" class="td-xl">
                  <TagsBadgeList
                    :tags="track.tags"
                    @click.prevent="openParameterSelector('showTagsSelector','modalTags', [track])"
                  />
                </td>

                <td :key="`property-${prop.name}`" v-if="prop.name === 'bpm'" class="td-m">
                  <NumberInput
                    :originalValue="track.bpm"
                    @onValueChanged="updateTrackField([track],'bpm',$event)"
                    :min="0"
                    :max="300"
                    class="bpm"
                    placeholder="Bpm"
                  />
                </td>

                <td :key="`property-${prop.name}`" v-if="prop.name === 'year'" class="td-m">
                  <NumberInput
                    :originalValue="track.year"
                    :min="1900"
                    :max="maxYear"
                    @onValueChanged="updateTrackField([track],'year',$event)"
                    class="year"
                    placeholder="Year"
                  />
                </td>

                <td :key="`property-${prop.name}`" v-if="prop.name === 'mood'">
                  <MultiChoiceTextInput
                    :originalValue="track.mood"
                    :enumValues="Constants.moods"
                    @onValueChanged="updateTrackField([track],'mood',$event)"
                  />
                </td>

                <td :key="`property-${prop.name}`" v-if="prop.name === 'energy'">
                  <MultiChoiceTextInput
                    :originalValue="track.energy"
                    :enumValues="Constants.energies"
                    @onValueChanged="updateTrackField([track],'energy',$event)"
                  />
                </td>

                <td :key="`property-${prop.name}`" v-if="prop.name === 'gender'">
                  <MultiChoiceTextInput
                    :originalValue="track.genderId"
                    :enumValues="Constants.genders"
                    @onValueChanged="updateTrackField([track],'genderId',$event)"
                  />
                </td>

                <td :key="`property-${prop.name}`" v-if="prop.name === 'isExplicit'">
                  <ToggleInput
                    :originalValue="track.isExplicit"
                    :id="`tracklist-toggle-${track.uid}`"
                    @onValueChanged="updateTrackField([track],'isExplicit',$event)"
                  />
                </td>

                <td :key="`property-${prop.name}`" v-if="prop.name === 'duration'" class="td-m">
                  <DurationText :duration="track.duration" />
                </td>

                <td :key="`property-${prop.name}`" v-if="prop.name === 'publicationDate'" class="td-l">
                  <span
                    class="date-added"
                  >{{ formatDate(track.publicationDate).format('YYYY-MM-DD') }}</span>
                </td>
              </template>

              <td class="td-actions">
                <div class="td-actions-wrapper">
                  <a
                    href="#"
                    @click.prevent="redirectToFullEdit(track)"
                  >
                    <i :class="['fas', 'fa-edit']" v-tooltip="{data: 'Full edit', arrowPosition: 'right'}"></i>
                  </a>
                  <a
                    href="#"
                    @click.prevent="openParameterSelector('showDeleteConfirmation','modalRemoval', [track])"
                  >
                    <i :class="['fas', 'fa-trash']"></i>
                  </a>

                  <router-link
                    tag="a"
                    :to="{name:'TrackMix', params: {stationUid:$route.params.stationUid, trackUid:track.uid}}"
                  >
                    <i class="fas fa-random" :class="{'mixValidate': track.isMixValidated}" v-tooltip="{data: `Mix points ${!track.isMixValidated ? 'not' : ''} validated`, arrowPosition: 'right'}"></i>
                  </router-link>

                  <div v-if="track.status !== 1" class="filled">
                    <i v-if="!track.isFilled" class="fas fa-exclamation-triangle filled-toggle"></i>
                    <i v-else class="fas fa-check" v-on:click="onValidate([track])"></i>

                    <div v-if="!track.isFilled" class="filled-dropdown">
                      <i class="fas fa-exclamation-triangle"></i>
                      Missing field{{ track.warnings && track.warnings.length > 1 ? 's' : '' }}:
                      <strong
                        v-html="getWarningText(track)"
                      ></strong>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>

          <tfoot class="sticky" v-show="hasStickyFooter">
            <tr>
              <th role="rowheader">
                <a
                  href="#"
                  class="table-footer-button"
                  v-on:click.prevent="removeAllSelectedTracks"
                >
                  <i class="far fa-check-square fas-xl"></i>
                </a>
              </th>

              <template v-for="prop in visibleListProperties">
                <th :key="`property-${prop.name}`" v-if="prop.name === 'title'" colspan="2">
                  <span class="table-footer-text">
                    <span class="table-footer-count">{{ selectedFileData.length }}</span>
                    track(s) selected
                  </span>
                </th>

                <th :key="`property-${prop.name}`" v-if="prop.name === 'artist'">
                  <TextInput
                    :originalValue="null"
                    @onValueChanged="updateTrackField('selected','artist',$event)"
                    placeholder="Artist name"
                  />
                </th>

                <th :key="`property-${prop.name}`" v-if="prop.name === 'artists'">
                  <a
                    href="#"
                    @click.prevent="openParameterSelector('showArtistSelector','modalArtists', selectedFileData.tracks)"
                  >Artist</a>
                </th>

                <th :key="`property-${prop.name}`" v-if="prop.name === 'styles'">
                  <a
                    href="#"
                    @click.prevent="openParameterSelector('showStyleSelector','modalStyles', selectedFileData.tracks)"
                  >Styles</a>
                </th>

                <th :key="`property-${prop.name}`" v-if="prop.name === 'languages'">
                  <a
                    href="#"
                    @click.prevent="openParameterSelector('showLanguageSelector','modalLanguages', selectedFileData.tracks)"
                  >Languages</a>
                </th>

                <th :key="`property-${prop.name}`" v-if="prop.name === 'label'">
                  <a
                    href="#"
                    @click.prevent="openParameterSelector('showLabelSelector','modalLabels',  selectedFileData.tracks)"
                  >Labels</a>
                </th>

                <th :key="`property-${prop.name}`" v-if="prop.name === 'description'">
                  <TextInput
                    :originalValue="null"
                    @onValueChanged="updateTrackField('selected','description',$event)"
                    placeholder="Info"
                  />
                </th>

                <th :key="`property-${prop.name}`" v-if="prop.name === 'tags'">
                  <a
                    href="#"
                    @click.prevent="openParameterSelector('showTagsSelector','modalTags',  selectedFileData.tracks)"
                  >Tags</a>
                </th>

                <th :key="`property-${prop.name}`" v-if="prop.name === 'bpm'">
                  <NumberInput
                    :originalValue="null"
                    :min="0"
                    :max="300"
                    @onValueChanged="updateTrackField('selected','bpm',$event)"
                    placeholder="Bpm"
                    class="bpm"
                  />
                </th>

                <th :key="`property-${prop.name}`" v-if="prop.name === 'year'">
                  <NumberInput
                    :originalValue="null"
                    :min="1900"
                    :max="maxYear"
                    @onValueChanged="updateTrackField('selected','year',$event)"
                    class="year"
                    placeholder="Year"
                  />
                </th>

                <th :key="`property-${prop.name}`" v-if="prop.name === 'mood'">
                  <MultiChoiceTextInput
                    ref="multiMood"
                    :originalValue="null"
                    :enumValues="Constants.moods"
                    @onValueChanged="updateTrackField('selected','mood',$event)"
                  />
                </th>

                <th :key="`property-${prop.name}`" v-if="prop.name === 'energy'">
                  <MultiChoiceTextInput
                    ref="multiEnergy"
                    :originalValue="null"
                    :enumValues="Constants.energies"
                    @onValueChanged="updateTrackField('selected','energy',$event)"
                  />
                </th>

                <th :key="`property-${prop.name}`" v-if="prop.name === 'gender'">
                  <MultiChoiceTextInput
                    :originalValue="null"
                    :enumValues="Constants.genders"
                    @onValueChanged="updateTrackField('selected','genderId',$event)"
                  />
                </th>

                <th :key="`property-${prop.name}`" v-if="prop.name === 'isExplicit'">
                  <ToggleInput
                    :originalValue="false"
                    :id="`tracklist-toggle-selectedFiles`"
                    :invert="true"
                    @onValueChanged="updateTrackField('selected','isExplicit',$event)"
                  />
                </th>

                <th :key="`property-${prop.name}`" v-if="prop.name === 'duration'"></th>

                <th :key="`property-${prop.name}`" v-if="prop.name === 'publicationDate'"></th>
              </template>

              <th class="table-footer__btn--container">
                <a
                  class="table-footer-button"
                  href="#"
                  @click.prevent="editTracksMeta"
                >
                  <i class="fas fa-edit fas-l"></i>
                </a>

                <a
                  class="table-footer-button"
                  href="#"
                  @click.prevent="openParameterSelector('showDeleteConfirmation','modalRemoval', selectedFileData.tracks)"
                >
                  <i class="fas fa-trash fas-l"></i>
                </a>

                <a
                  class="table-footer-button"
                  href="#"
                  @click.prevent="editTracksMix"
                >
                  <i class="fas fa-random fas-l"></i>
                </a>

                <a class="table-footer-button" href="#">
                  <i
                    v-on:click="onValidateMultiple(selectedFiles.tracks)"
                    class="fas fa-check fas-l"
                  ></i>
                </a>
              </th>
            </tr>
          </tfoot>
        </table>

        <SelectorTags
          v-show="viewState.showTagsSelector"
          ref="modalTags"
          :stationUid="currentStationUid"
          @onHidden="resetModalState('showTagsSelector')"
        />
        <SelectorArtist
          v-show="viewState.showArtistSelector"
          ref="modalArtists"
          :stationUid="currentStationUid"
          @onHidden="resetModalState('showArtistSelector')"
        />
        <SelectorStyle
          v-show="viewState.showStyleSelector"
          :isOpen="viewState.showStyleSelector"
          ref="modalStyles"
          :stationUid="currentStationUid"
          @onHidden="resetModalState('showStyleSelector')"
        />
        <SelectorLanguages
          v-show="viewState.showLanguageSelector"
          ref="modalLanguages"
          :stationUid="currentStationUid"
          @onHidden="resetModalState('showLanguageSelector')"
        />
        <SelectorLabels
          v-show="viewState.showLabelSelector"
          ref="modalLabels"
          :multiselect="false"
          :stationUid="currentStationUid"
          @onHidden="resetModalState('showLabelSelector')"
        />
        <TrackRemovalCard
          v-show="viewState.showDeleteConfirmation"
          ref="modalRemoval"
          :stationUid="currentStationUid"
          @onHidden="resetModalState('showDeleteConfirmation')"
        />
      </div>
      <TrackListNotFound v-else-if="isSearchTriggered" />
      <TrackListEmptyCard v-else />
    </template>
  </div>
</template>

<script>
import Vue from "vue";
import Constants from "@/store/constants.js";
import EventBus from "@/eventbus";
import Apis from "@/apis/index";
import { HTTP } from "@/utils/http";
import SelectorArtist from "./SelectorArtist.vue";
import SelectorStyle from "./SelectorStyle.vue";
import SelectorLanguages from "./SelectorLanguages.vue";
import SelectorLabels from "./SelectorLabels.vue";
import SelectorTags from "../Library/SelectorTags.vue";
import TagsBadgeList from "@/components/common/TagsBadgeList.vue";
import ArtistBadgeList from "@/components/common/ArtistBadgeList.vue";
import StyleBadgeList from "@/components/common/StyleBadgeList.vue";
import LanguageBadgeList from "@/components/common/LanguageBadgeList.vue";
import TextInput from "@/components/common/TextInput.vue";
import ToggleInput from "@/components/common/ToggleInput.vue";
import DurationText from "@/components/common/DurationText.vue";
import NumberInput from "@/components/common/NumberInput.vue";
import LabelTextInput from "@/components/common/LabelTextInput.vue";
import MultiChoiceTextInput from "@/components/common/MultiChoiceTextInput.vue";
import moment from "moment";
import TrackRemovalCard from "@/components/Library/TrackRemovalCard.vue";
import WaitingBlock from "@/components/common/WaitingBlock.vue";
import TrackListEmptyCard from "./TrackListEmptyCard.vue";
import PlayButton from "@/components/common/PlayButton.vue";
//import MoveTracks from "@/components/common/MoveTracks";
import TrackListNotFound from "@/components/Library/TrackListNotFound";
import { mapState } from "vuex";
import Tooltip from "@/components/common/Tooltip.vue";
import {safeObj} from "@/utils/safeObj";

export default {
  name: "TrackList",
  components: {
    Tooltip,
    TrackListNotFound,
    //MoveTracks,
    SelectorTags,
    SelectorArtist,
    SelectorStyle,
    SelectorLanguages,
    SelectorLabels,
    TagsBadgeList,
    ArtistBadgeList,
    StyleBadgeList,
    LanguageBadgeList,
    TextInput,
    ToggleInput,
    MultiChoiceTextInput,
    LabelTextInput,
    NumberInput,
    DurationText,
    WaitingBlock,
    TrackRemovalCard,
    TrackListEmptyCard,
    PlayButton
  },
  watch: {
    fileType: function() {
      console.log("FileType changed");
      this.sort.field = 'publicationDate';
      this.sort.ascending = false;
      if (this.$route.query.search) {
        this.searchTracks(this.$route.query.search)
      } else {
        EventBus.$emit("closeSearch");
        this.loadTracks();
      }
    },
    hasSearch: {
      handler: function (newV) {
        if(!newV){
          this.isSearchTriggered = false;
        }
      },
      immediate: true
    },
    "$route.params.stationUid": {
      handler: function(value, oldValue) {
        this.loadTracks();
      }
    },
    isDeletedTrack: {
      handler: function (newV) {
        if (newV) {
          this.loadTracks();
        }
      }
    }
  },
  created() {
    EventBus.$on("onSearchClose", this.closeSearch);
    EventBus.$on("onTracksSearch", this.searchTracks);
    EventBus.$on("onTrackUploaded", this.onTrackUploaded);
  },
  beforeDestroy() {
    EventBus.$off("onSearchClose", this.closeSearch);
    EventBus.$off("onTracksSearch", this.searchTracks);
    EventBus.$off("onTrackUploaded", this.onTrackUploaded);
  },
  mounted() {
    if (! this.fileType) {
      this.$router.push({ params: { fileType: 'MUSIC' }})
      return
    }

    this.loadTracks();
  },
  computed: {
    ...mapState({
      hasSearch: state => state.viewState.hasSearch,
      isDeletedTrack: state => state.tracks.isDeletedTrack
    }),
    Constants: () => Constants,
    fileType () {
      return this.$route.params.fileType
    },
    visibleListProperties () {
      return Constants.trackListProperties.filter(prop => prop.fileTypes === 'all' || prop.fileTypes.includes(this.fileType))
    },
    maxYear: () => new Date().getFullYear(),
    currentStationUid() {
      return this.$route.params.stationUid;
    },
    selectedFileData: function() {
      return {
        length: Object.keys(this.selectedFiles.tracks).length,
        tracks: Object.values(this.selectedFiles.tracks)
        // trackUids: Object.keys(this.selectedFiles.tracks) || []
      };
    },
    hasStickyFooter: function() {
      return this.selectedFileData.length > 1;
    },
    trackTypesIcons() {
      const typesIcons = []

      Object.values(Constants.trackTypes).forEach((type) => {
        typesIcons[type.value] = type
      })

      return typesIcons
    }
  },
  data() {
    return {
      viewState: {
        isLoadingTracks: false,
        isSorting: false,
        isRemovingTracks: false,
        isInSearch: false,
        showTagsSelector: false,
        showArtistSelector: false,
        showLanguageSelector: false,
        showStyleSelector: false,
        showLabelSelector: false,
        showDeleteConfirmation: false,
        showEditMix: false
      },
      shiftSelection:
      {
          startIndex:null, endIndex:null, isSelected:false
      },
      sort: {
        field: 'publicationDate',
        ascending: false
      },
      fileList: [],
      selectedFiles: {
        tracks: {},
        updateTime: new Date()
      },
      cart: [],
      timeout: null,
      isSearchTriggered: false,
      requestParams: ''
    };
  },
  updated() {
    const $portletBody = document.querySelector(".m-portlet__body");

    if (this.selectedFileData) {
      if (this.selectedFileData.length > 1) {
        $portletBody.classList.add("has-footer");
      } else {
        $portletBody.classList.remove("has-footer");
      }
    }
  },
  methods: {
    redirectToFullEdit(track){
      this.$router.push({ name:'TrackHome', params: { stationUid: this.$route.params.stationUid, trackUid: track.uid }});
      console.log('redirect: ', track);
    },
    // Cue points
    editCuePoints(track) {
      this.$router.push({ name: "CuePoints", params: { trackId: track.uid } });
    },
    editCuePointsMulti(tracks) {
      const firstTrackId = this.selectedFileData.tracks[0].uid;
      this.$router.push({
        name: "CuePoints",
        params: { trackId: firstTrackId },
        query: { track_ids: JSON.stringify(this.tracks.map(t => t.uid)) }
      });
    },
    formatDate(val) {
      return moment(val);
    },
    resetModalState(toggler) {
      this.$set(this.viewState, toggler, false);
    },
    openParameterSelector(toggler, selectorName, tracks) {
      if (!tracks || tracks.length === 0) return;
      this.viewState[toggler] = true;
      try {
        var modalSelector = this.$refs[selectorName];
        if (modalSelector) {
          modalSelector.editForTracks(tracks);
        }
      } catch (e) {
        console.log("exc:", e);
      }
    },
    // Selection
    toggleSelection: function() {
      if (this.selectedFileData.length === 0) {
        let selection = {};
        this.fileList.forEach(tr => {
          selection[tr.uid] = tr;
        });
        this.selectedFiles.tracks = selection;
      } else {
        this.selectedFiles.tracks = {};
      }
    },
    removeAllSelectedTracks() {
      this.selectedFiles.tracks = {};
    },
    multipleToggle(){
      let multiSelect = {
          startIndex:Math.min(this.shiftSelection.startIndex, this.shiftSelection.endIndex),
          endIndex:Math.max(this.shiftSelection.startIndex, this.shiftSelection.endIndex),
          isSelected:this.shiftSelection.isSelected
      }
      console.log(JSON.stringify(multiSelect))
      let i = 0
      this.fileList.filter(tr => {
          let elligible = i>= multiSelect.startIndex && i<=multiSelect.endIndex;
          console.log(i,  elligible)
          i++;
          return elligible;
      })
      .map(track =>
      {
          track.isSelected = this.shiftSelection.isSelected;
          if (this.shiftSelection.isSelected) {
            this.$set(this.selectedFiles.tracks, track.uid, track);
          } else {
            this.$delete(this.selectedFiles.tracks, track.uid);
          }
      })
    },
    toggleTrack(track, event,index) {
      track.isSelected = !track.isSelected;
      if (event.shiftKey){
          if (this.shiftSelection.startIndex != null){
              this.shiftSelection.endIndex = index;
              this.multipleToggle()
              this.shiftSelection = {isSelected:track.true, startIndex:null, endIndex:null}
              return;
          }
          else
          {
              console.log("End SHIFT Index")
              this.shiftSelection.isSelected = track.isSelected;
              this.shiftSelection.startIndex = index;
              this.shiftSelection.endIndex = null;
          }
      }
      else{
          this.shiftSelection = {isSelected:track.true, startIndex:null, endIndex:null}
          console.log("Set SHIFT Operation", JSON.stringify(this.shiftSelection))
      }
      if (this.selectedFiles.tracks[track.uid] != null) {
        Vue.delete(this.selectedFiles.tracks, track.uid);
      } else {
        this.$set(this.selectedFiles.tracks, track.uid, track);
      }
    },
    // Edition
    editTracksMeta() {
      const tracks = [];

      Object.keys(this.selectedFiles.tracks).map(item => {
        tracks.push(this.selectedFiles.tracks[item].uid);
      });

      this.$store.commit("tracks/updateTracks", tracks);
      this.$store.commit("tracks/updatePrevPage", this.$route);
      this.$store.commit("tracks/updateClose", true);

      this.$router.push({
        name: "TrackHome",
        params: {
          stationUid: this.$route.params.stationUid,
          trackUid: tracks[0]
        }
      });
    },
    editTracksMix() {
      const tracks = [];

      Object.keys(this.selectedFiles.tracks).map(item => {
        tracks.push(this.selectedFiles.tracks[item].uid);
      });

      this.$router.push({
        name: "TrackMix",
        params: {
          stationUid: this.$route.params.stationUid,
          trackUid: tracks[0]
        },
        query: {
          track_ids: JSON.stringify(tracks)
        }
      });
    },
    onTrackUploaded(newTrack) {
      if(safeObj(newTrack)){
        if (newTrack.typeID !== Constants.trackTypes[this.fileType].value) {
          const newType = Object.values(Constants.trackTypes).find(type => type.value === newTrack.typeID)
          this.$router.push({ name:'LibraryPending', query: this.$router.query, params: { fileType: newType.key }})

          return
        }
        this.fileList = [newTrack, ...this.fileList];

        this.$store.dispatch('Toasts/addToast', {
          title: 'Success',
          message: 'Files successfully uploaded',
          delay: 3000,
          type: 'success'
        });
      }
    },
    loadTracks(isSorting) {
      this.removeAllSelectedTracks();

      try {
        if (!isSorting) {
          this.fileList = [];
          this.viewState.isLoadingTracks = true;
        } else {
          this.viewState.isSorting = true;
        }

        var api = Apis.library.tracks.search;

        this.requestParams = {
          stationUid: this.currentStationUid,
          sortField:  this.sort.field,
          sortAscending: this.sort.ascending,
          trackType: this.fileType
        };

        HTTP.post(Apis.library.tracks.pending, this.requestParams)
          .then(
            response => {
              this.fileList = response.data.dataItem.items.filter(item => item.typeID === Constants.trackTypes[this.fileType].value);
              this.$store.commit('tracks/isDeletedTrack', false)
            },
            error => {
              console.log("Error -> track list ->", error.response);
            }
          )
          .then(() => {
            this.viewState.isLoadingTracks = false;
            this.viewState.isSorting = false;
          });
      } catch (e) {
        console.log(e);
        this.viewState.isLoadingTracks = false;
        this.viewState.isSorting = false;
      }
    },
    closeSearch() {
      if (this.viewState.isInSearch) {
        this.viewState.isInSearch = false;
        this.loadTracks();
      }
    },
    async searchTracks(terms) {
      if (terms === null) {
        this.loadTracks();
        return;
      }

      this.removeAllSelectedTracks();
      this.fileList = [];
      this.viewState.isLoadingTracks = true;
      this.isSearchTriggered = false;

      try {
        const response = await HTTP.post("/api/library/tracks/pending", {
          trackType: this.fileType,
          stationUid: this.$route.params.stationUid,
          sortField: "title",
          sortAscending: true,
          pageSize: 100,
          page: 0,
          isAdvanced: false,
          searchTerm: terms
        });

        if (response) {
          this.isSearchTriggered = true;
          this.fileList = response.data.dataItem.items;
        }
      } catch (e) {
        console.log("error in search :", e);
      }

      this.viewState.isLoadingTracks = false;
    },
    async updateTrackField(tracksToUpdate, field, value) {
      let tracks = tracksToUpdate;

      if (tracks === "selected") {
        tracks = [];

        Object.keys(this.selectedFiles.tracks).map(prop => {
          tracks.push(this.selectedFiles.tracks[prop]);
        });
      }

      if (field === 'year' && (!value || Number(value) < 1900 || value > this.maxYear)) {
        this.$store.dispatch('Toasts/addToast', {
          title: 'Year is not valid',
          message: `The year is not valid. It must be a valid year between 1900 and ${this.maxYear}`,
          type: 'danger'
        });

        return false
      }

      try {
        const result = await HTTP.post(Apis.library.tracks.update[field], {
          stationUid: this.currentStationUid,
          trackUids: tracks.map(x => x.uid),
          value: value
        });

        console.log("update trackfield :", result);

        if (result.data.dataItem != null) {
          tracks.map(track => {
            // Find updated data in result
            let updatedTrack = result.data.dataItem[track.uid];
            if (updatedTrack) {
              track.isFilled = updatedTrack.isFilled; // Needed
              track.warnings = updatedTrack.warnings;
              track[field] = value;
            }
          });
        }
      } catch (e) {
        var errorResult = e.response;
      }

      for (let refName in ['multiMood', 'multiEnergy']) {
        if (Object.keys(this.$refs).includes(refName)) {
          this.$refs[refName].resetValue();
        }
      }
    },
    trackUpdated(newTrack) {
      this.fileList.map(track => {
        if (track.uid === newTrack.uid) {
          return newTrack;
        }

        return track;
      });

      if (this.selectedFiles.tracks[newTrack.uid]) {
        this.$set(this.selectedFiles.tracks, newTrack.uid, newTrack);
      }
    },
    // Order
    order(type) {
      if (type === this.sort.field) {
        this.sort.ascending = !this.sort.ascending;
        this.loadTracks(true);
        return;
      }

      this.sort.field = type;
      this.sort.ascending = true;
      this.loadTracks(true);
    },
    // Validation
    onValidateMultiple(tracks) {
      const tcks = [];

      Object.keys(tracks).map(prop => {
        tcks.push(tracks[prop]);
      });

      this.onValidate(tcks);
    },
    async onValidate(tracks) {
      const trackUids = tracks
        .filter(track => track.isFilled)
        .map(track => track.uid);

      if (trackUids.length === 0) {
        return;
      }

      try {
        const response = await HTTP.post(Apis.library.tracks.update.validate, {
          stationUid: this.$store.state.currentStationUid,
          trackUids,
          value: 0
        });

        // const notification = {
        //   message: `${trackUids.length} track${
        //     trackUids.length > 1 ? "s" : ""
        //   } validated`,
        //   class: "success",
        //   icon: "fa-check"
        // };

        await this.$store.dispatch('Toasts/addToast', {
          title: 'Success',
          message: `${trackUids.length} track${trackUids.length > 1 ? "s" : ""} validated`,
          type: 'success'
        });

        this.loadTracks();
        EventBus.$emit("reFreshLeftMenu");
        //this.removeFilesFromList(trackUids);
      } catch (error) {
        console.log("error validate :", error);
      }
    },
    removeFilesFromList(uids) {
      this.fileList.filter(item => uids.include(item.uid))
      this.selectedFiles.items.filter(item => uids.include(item.uid))
      // uids.map(uid => {
      //   const track = this.fileList.filter(item => item.uid === uid)[0];
      //   const index = this.fileList.indexOf(track);
      //
      //   this.$delete(this.fileList, index);
      //   this.$delete(this.selectedFiles.tracks, uid);
      // });
    },
    // Warnings
    getWarningText(track) {
      if (!!track.warnings) {
        return track.warnings
          .map(item =>
            item
              .replace(/TRACK_ERROR_|_REQUIRED/g, "")
          )
          .join(", ")
          .toLowerCase();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
/* KEEP IT */

tfoot tr th:first-child {
  /* Alignment for multiselection checkbox*/
  text-align: right;
  padding-right: 20px;
}

/**************************************/

div.content-list {
  height: 100%;
  width: 100%;
  position: relative;
  padding-bottom: 50px;
}

table {
  width: 100%;
  max-height: 100%;
  margin-bottom: 0;

  &.is-sorting {
    tbody {
      opacity: 0.5;
      pointer-events: none;
    }
  }
}

thead {
  position: -webkit-sticky;
  /* Safari */
  position: sticky;
  z-index: 1000;
  top: 0;
  background: white; // border-bottom: 2px solid #d2d2d2;

  th {
    border-bottom: 0;

    &::after {
      content: "";
      position: absolute;
      left: 0;
      bottom: 0;
      width: 100%;
      height: 1px;
      background-color: #d2d2d2;
    }
  }
}

tbody {
  background-color: #fff;
  tr.shiftable {
    td, th {
      border-bottom:1px solid var(--COLOR_BRAND) !important;
    }
    border-bottom:1px solid var(--COLOR_BRAND) !important;
  }
  tr:first-child th,
  tr:first-child td {
    border-top: 0;
  }
}

tfoot tr,
tfoot tr th,
tfoot tr td {
  vertical-align: middle;
  border-top: 1px solid #d2d2d2;
}

thead tr,
thead tr th,
tfoot tr {
  font-size: 13px;
  line-height: 16px;
}

tfoot {
  font-weight: bold;
}

td {
  padding: 0 12px;
  /* Keep it : to have more data lines on screen */
  vertical-align: middle;

  &:not(:last-child) {
    border-right: 1px dashed #d2d2d2;
  }

  &.td-m {
    min-width: 80px;
  }

  &.td-xl {
    min-width: 250px;
  }

  &.td-actions {
    &:hover {
      z-index: 801;
    }

    .td-actions-wrapper {
      display: flex;

      > *:not(:first-child) {
        margin-left: 8px;
      }
    }

    a {
      transition: opacity 0.15s ease-in-out;
      opacity: 0.5;

      &:hover {
        opacity: 1;
      }
    }

    i {
      &.fa-exclamation-triangle {
        color: #fe6b5b;
      }
      &.fa-check {
        color: #89c42c;
      }
    }

    .filled {
      position: relative;

      .filled-toggle {
        &:hover {
          & + .filled-dropdown {
            display: block;
          }
        }
      }

      .filled-dropdown {
        display: none;
        position: absolute;
        top: calc(100% + 5px);
        right: 0;
        width: 300px;
        padding: 10px 15px 17px;
        border-radius: 5px;
        box-shadow: 0 1px 21px 2px rgba(0, 0, 0, 0.2);
        background-color: #fff;
        font-size: 14px;
        line-height: 1.2;

        span {
          white-space: normal;
        }

        i {
          display: inline-block;
          opacity: 1;
          margin-right: 2px;
          margin-bottom: -15px;
        }

        // Extend hover surface
        &::after {
          content: "";
          position: absolute;
          top: -12px;
          left: 0;
          width: 100%;
          height: 13px;
        }

        &:hover {
          display: block;
        }
      }
    }
  }
}

tr {
  .fa-play {
    color: transparent;
  }
}

tr:hover .fa-play {
  color: #aaaaaa;
}

thead th,
tbody th {
  vertical-align: middle;
  color: #aaaaaa;
  font-size: 13px;
  line-height: 16px;
}

thead th {
  border-top: 0;
  padding-top: 15px;

  &:not(:first-child):not(:last-child) {
    color: #292c33;
  }

  .sort {
    display: flex;
    cursor: pointer;
    transition: color 0.15s ease-in-out;
    color: #aaaaaa;
    font-weight: 400;

    &[disabled] {
      pointer-events: none;
    }

    &.is-active {
      color: #000;
    }

    &:hover {
      text-decoration: none;
    }

    &-icons {
      display: flex;
      flex-direction: column;
      position: relative;
      margin-left: 5px;

      i {
        font-size: 10px;
        height: 8px;
      }
    }

    @keyframes sorting {
      0% {
        transform: rotate(0);
      }
      100% {
        transform: rotate(360deg);
      }
    }

    .fa-sync-alt {
      align-self: center;
      font-size: 12px;
      margin-left: 5px;
      transform-origin: 50% 50%;
      animation: sorting 1s linear infinite;
    }
  }
}

thead th:first-child,
tbody th:first-child {
  text-align: right;
  background: white;
  vertical-align: middle;
  position: -webkit-sticky;
  /* Safari */
  position: sticky;
  left: 0;
  z-index: 800; // padding-left: 0px;

  &::before {
    content: "";
    position: absolute;
    right: 0;
    top: 0;
    width: 1px;
    height: 100%;
    background-color: #d2d2d2;
  }
}

thead th:first-child {
  z-index: 1600;
}

thead th:last-child,
tbody td:last-child {
  max-height: 100%;
  vertical-align: middle;
  position: -webkit-sticky;
  /* Safari */
  position: sticky;
  right: 0;
  z-index: 800;
  background: #fff;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    width: 1px;
    height: 100%;
    background-color: #d2d2d2;
  }
}

thead th:last-child,
tfoot th:last-child {
  position: sticky;
  right: 0;
  z-index: 1600;
}

thead th[role="rowheader"] i,
tbody td:last-child i,
tbody th[role="rowheader"] i {
  line-height: 30px;
  display: inline;
  padding: 10px;
  cursor: pointer;
}

thead th:last-child {
  background: white;
}

tbody tr[role="rowheader"] i,
tbody td:last-child a {
  text-decoration: none;
}

td input[type="text"] {
  width: calc(100% - 20px);
  font-size: 1rem;
  line-height: 1.5;
  color: #495057;
  background-color: transparent;
  background-clip: padding-box;
  outline: none;
  &:focus {
    background-color: rgba(#fff, 0) !important;
    border-bottom: 1px solid;
  }
}

col.col-l {
  min-width: 20em;
}

col.col-s {
  min-width: 10em;
}

col.col-xs {
  width: 5em;
}

col.actions {
  background: #495057;
}

input.bpm,
input.year {
  width: 4em;
}

span {
  white-space: nowrap;
}

span.play-button {
  margin-right: 5px;
}

span.item-bar {
  position: absolute;
  left: 0;
  width: 2.5px;
  display: inline-block;
  border-radius: 0 5px 5px 0;
  height: 30px;
  background: #fe6b5b;
}

span.item-bar.invalid {
  background-color: #fe6b5b;
}

span.item-bar.valid {
  background-color: #89c42c;
}

// .middle-link {
//   margin: 0 10px;
// }

.date-added {
  font-weight: 400;
}

.table-footer {
  display: flex;
  position: fixed;
  z-index: 801;
  left: 300px;
  right: 0;
  bottom: 0;
  border-radius: 0 0 3px 3px;
  align-items: center;
  justify-content: space-between;
  overflow: auto;
  .td {
    flex-shrink: 0;
    padding: 16px 12px;
    vertical-align: middle;
    color: #fff;
    &:first-child {
      padding-left: 15px;
    }
    &:last-child {
      display: flex;
      min-width: 142px;
      padding-left: 22px;
      a + a {
        margin-left: 20px;
      }
    }
    button.option {
      color: #fff;
    }
  }
  .fas {
    &.fas-l {
      font-size: 18px;
    }
    &.fas-xl {
      font-size: 24px;
    }
  }
}

.table-footer-left,
.table-footer-right {
  display: flex;
  align-items: center;
  padding: 20px 15px;
  color: #fff;
  .fas {
    display: block;
  }
}

.table-footer-right {
  .table-footer-button {
    margin-left: 20px;
  }
}

.table-footer-text {
  color: #fff;
  font-size: 14px;
  font-weight: bold;
  line-height: 19px;
}

.table-footer-count {
  display: inline-block;
  padding: 2px 10px 4px;
  margin-right: 4px;
  border-radius: 20px;
  background-color: rgba(#000, 0.2);
}

.table-footer-button {
  display: inline-block;
  transition: opacity 0.2s ease-in-out;
  color: #fff;
  text-decoration: none;
  cursor: pointer;
  vertical-align: middle;
  &:hover {
    opacity: 0.6;
  }
  + .table-footer-text {
    margin-left: 23px;
  }
}

// Transitions
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>

<style lang="scss">
.td,
td {
  &.td-m {
    min-width: 80px;
  }
  &.td-l {
    min-width: 110px;
  }
  &.td-xl {
    min-width: 250px;
  }
}

.table-footer {
  .td {
    a:not(.table-footer-button),
    input:not(.toggle-input),
    input::placeholder {
      display: block;
      width: 100%;
      padding: 0.375rem 0.75rem 0.375rem 0;
      color: #fff;
      font-weight: 400;
    }
    a:not(.table-footer-button),
    input:not(.toggle-input) {
      border-bottom: 1px solid #fff;
      text-decoration: none;
    }
    .toggle {
      padding: 0;
    }
  }
}

.table-footer-button {
  font-size: 16px;
  margin-left: 8px;

  &:first-of-type {
    margin-left: 0;
  }
}

.type-icon {
  color: #d2d2d2;
}
  .mixValidate{
    color: var(--success)
  }
</style>
